import * as d3 from "d3";
import { observer } from "mobx-react-lite";

import { useEffect, useState } from "react";
import moment from "moment";
import { Button } from "antd";

import "./Chart.css";

function Chart(props) {
  const [viewBox, setViewBox] = useState("0 0 500 270");
  const [redraw, setRedraw] = useState(false);
  const [lines, setLines] = useState([]);
  const [visibility, setVisibility] = useState({});
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [timestamps, setTimestamps] = useState([]);
  const [margin] = useState({
    top: 10,
    right: 10,
    bottom: 30,
    left: 20,
  });

  const colorMap = ["#6296E2", "#9562e2"];

  useEffect(() => {
    setViewBox(`0 0 ${props.width} ${props.height}`);
    setHeight(props.height - margin.top - margin.bottom);
    if (!props.store.height === 0) {
      props.store.height = props.height - margin.top - margin.bottom;
    }
    setWidth(props.width - margin.left - margin.right);

    // X-Axis
    let x = props.store.x();
    if (x === undefined) {
      return;
    }
    let xAxis = d3.selectAll(`.xaxis`);
    xAxis.call(d3.axisBottom(x));

    // Y-Axis
    let maxYDomain = 0;
    for (let row of series) {
      if (!row) {
        continue;
      }
      maxYDomain = Math.max(
        maxYDomain,
        d3.max(props.data, (d) => +d[row.id])
      );
    }

    const y = d3.scaleLinear().range([height, 0]).domain([0, maxYDomain]);
    let yAxis = d3.select(`#yaxis${props.id}`);
    yAxis.call(d3.axisLeft(y));

    // Brush
    const brush = d3.brushX().extent([
      [0, 0],
      [width, height],
    ]);

    // Area generator
    const areaGenerator = d3
      .line()
      .x((d) => x(new Date(d.timepoint)))
      .y((d) => y(d[`${props.id}`]));

    const lines = [];

    for (let row of series) {
      if (!row) {
        continue;
      }

      const key = row.id;
      const areaGenerator2 = d3
        .line()
        .x((d) => x(new Date(d.timepoint)))
        .y((d) => y(d[key]));

      if (!visibility.hasOwnProperty(row.id)) {
        visibility[row.id] = true;
      }
      setVisibility(visibility);
      lines.push(areaGenerator2(props.data));
    }
    setLines(lines);

    const area = d3.select(`#area${props.id}`);

    const onMouseMove = (e) => {
      const mousePosition = d3.pointer(e);
      const hoverDate = x.invert(mousePosition[0]);
      props.store.setCurrentTimestamp(hoverDate.getTime());
    };

    let group = d3.select(`#${props.id}`);
    group.on("mousemove", onMouseMove);

    const updateChart = (event) => {
      let idleTimeOut;
      const idled = () => {
        idleTimeOut = null;
      };
      let extent = event.selection;

      if (!extent) {
        if (!idleTimeOut) return (idleTimeOut = setTimeout(idled, 350)); // This allows to wait a little bit
        x.domain([4, 8]);
      } else {
        x.domain([x.invert(extent[0]), x.invert(extent[1])]);
        d3.select(`#brush${props.id}`).call(brush.move, null); // This remove the grey brush area as soon as the selection has been done
      }
      props.store.x = () => x;
      xAxis.transition().duration(1000).call(d3.axisBottom(x));
      area.select(`#myarea${props.id}`).attr("d", areaGenerator(props.data));
    };

    brush.on("end", updateChart);
    d3.select(`#brush${props.id}`).call(brush);

    const doubleClick = () => {
      x.domain(d3.extent(props.data, (d) => new Date(d.timepoint)));
      xAxis.transition().call(d3.axisBottom(x));
      const area = d3.select(`#area${props.id}`);
      props.store.x = () => x;
      area.select(`#myarea${props.id}`).attr("d", areaGenerator(props.data));
    };
    d3.select(`#${props.id}`).on("dblclick", doubleClick);

    // timepoints array
    setTimestamps(props.data.map((x) => new Date(x.timepoint).getTime()));
  }, [
    props.data,
    props.width,
    props.height,
    props.rects,
    props.events,
    margin,
    height,
    width,
    visibility,
    redraw,
  ]);

  const rectMouseOver = (e, d) => {
    let tooltip = d3.select(`#tooltip${props.id}`);
    tooltip.transition().duration("50").style("opacity", 1);
    let text = `Event Type: ${d.eventtype}<br/>
                    Event Start: ${moment(d.eventstart).format(
                      "MMMM Do YYYY, h:mm:ss a"
                    )}<br/>
                    Event End: ${moment(d.eventend).format(
                      "MMMM Do YYYY, h:mm:ss a"
                    )}<br/>`;
    tooltip
      .html(`${text}`)
      .style("left", e.pageX + 10 + "px")
      .style("top", e.pageY - 15 + "px")
      .style("padding", "20px")
      .style("z-index", 1);
  };

  const rectMouseOut = (e) => {
    let tooltip = d3.select(`#tooltip${props.id}`);
    tooltip
      .transition()
      .duration("50")
      .style("opacity", 0)
      .style("z-index", -1);
  };

  const toggleChartVisibility = (key) => {
    visibility[key] = !visibility[key];
    setVisibility(visibility);
    setRedraw(!redraw);
  };

  const series = props.series;

  return (
    <div className="charts__chart">
      <div className="tooltip-donut" id={`tooltip${props.id}`}></div>
      <div className="charts__title">
        <b>{props.title}</b>
      </div>
      <div className="charts__value">{props.lineValue}</div>
      <div>
        {series.map((x, i) => {
          return (
            <Button type="text" onClick={() => toggleChartVisibility(x.id)}>
              <span
                style={{
                  background: visibility[x.id] ? colorMap[i] : "#7a7a7a",
                  width: "10px",
                  height: "10px",
                  marginTop: "5px",
                  marginRight: "5px",
                  float: "left",
                }}
              ></span>
              {x.title}
            </Button>
          );
        })}
      </div>
      <svg
        className="line-chart"
        viewBox={viewBox}
        height={props.height}
        width={props.width + 10}
      >
        <g transform={`translate(${margin.left}, ${margin.top})`} id={props.id}>
          <g clipPath={`url(#clip${props.id})`} id={`area${props.id}`}>
            {lines.map((x, i) => {
              return visibility[series[i].id] ? (
                <path
                  style={{ stroke: colorMap[i] }}
                  className="line-chart__line"
                  id={`myarea-${props.id}-${i}`}
                  d={x}
                />
              ) : (
                <g />
              );
            })}
            ;<g id={`brush${props.id}`}></g>
          </g>
          <g
            transform={`translate(0, ${height})`}
            id={`xaxis${props.id}`}
            className="xaxis"
          ></g>
          <g id={`yaxis${props.id}`}></g>
          {props.rects &&
            props.rects.map((rect, i) => {
              return (
                <rect
                  x={rect.x}
                  y={rect.y}
                  height={rect.h}
                  width={rect.w}
                  className="charts__rect"
                  onMouseOver={(e) => rectMouseOver(e, rect.d)}
                  onMouseOut={rectMouseOut}
                  key={i}
                ></rect>
              );
            })}

          <defs>
            <clipPath id={`clip${props.id}`}>
              <rect height={height} width={width} x={0} y={0}></rect>
            </clipPath>
          </defs>
        </g>
      </svg>
    </div>
  );
}

export default observer(Chart);
